import { Component, inject } from '@angular/core';
import { MaterialModule } from '../../../../../../modules/material.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { SnackBarService } from '../../../../../../shared/services/snack-bar.service';
import { Attachment } from '../../../../../../shared/utils/types';
import { FILE_TYPES, MAX_FILE_SIZE } from '../../../../../../shared/utils/constant';
import { ThumbnailComponent } from "../../../../../../shared/components/thumbnail/thumbnail.component";

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [MaterialModule, CommonModule, ThumbnailComponent],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent {
  readonly dialogRef = inject(MatDialogRef<FileUploadComponent>);
  private readonly data = inject<any>(MAT_DIALOG_DATA);
  private snackBarService = inject(SnackBarService);
  files: Attachment[] = [];
  invalidFiles: string[] = [];
  isDragging = false;

  constructor() {}

  uploadFiles(): void {
    this.dialogRef.close({ files: this.files });
  }

  onFileSelected(event: Event) {
    const selectedFiles = (event.target as HTMLInputElement).files;
    this.invalidFiles = [];
    this.addFiles(selectedFiles);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    this.invalidFiles = [];
    const dataTransfer = event.dataTransfer;
    if (dataTransfer && dataTransfer.files) {
      this.addFiles(dataTransfer.files);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  addFiles(fileList: FileList) {
    let invalidFiles: string[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      // Check if the file is already added
      const fileExists = this.files.some(f => f.name === file.name);
      const uploadedFilesExist = this.data.attachmentFiles.some(f => f.name === file.name);
      if (fileExists || uploadedFilesExist) {
        const message = fileExists ? `The file "${file.name}" has already added.` : `The file "${file.name}" has already uploaded.`;
        this.snackBarService.error(message);
        continue; // Skip to the next file
      }
      // Check if the file size is too large
      if(file.size > MAX_FILE_SIZE) {
        this.snackBarService.error(`File size is too large: ${file.name}`);
        continue;
      }
      if (this.isValidFileType(file)) {
        const reader = new FileReader();
        reader.onload = (e: Event) => {
          this.files.push({
            name: file.name,
            type: file.type,
            original_url: (e.target as FileReader).result as string,
            thumbnail_url: (e.target as FileReader).result as string,
            file: file,
          });
        };
        reader.readAsDataURL(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      const message = invalidFiles.length === 1
        ? `Invalid file type: ${invalidFiles[0]}`
        : `${invalidFiles.length} files have invalid types and were rejected`;
      this.snackBarService.error(message);
      this.invalidFiles = invalidFiles;
    }
  }

  isValidFileType(file: File): boolean {
    const validTypes = [
      FILE_TYPES.Pdf,
      FILE_TYPES.Docx,
    ];
    return validTypes.includes(file.type) || file.type.startsWith(FILE_TYPES.Image);
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileMaxSize(): string {
    return this.formatFileSize(MAX_FILE_SIZE);
  }
}
